import {
	type CalibrationJSONObject,
	type CaptureCameraCalibration,
	type CaptureCapture as SchemaCaptureCapture,
	type CaptureFileJSONObject,
	type CaptureLocation,
	type CollectionFieldAnchorManualFieldValue,
} from '@ikegps/core-api';
import {
	type CollectionField as SchemaCollectionField,
	type CollectionFieldAnchor as SchemaCollectionFieldAnchor,
	type CollectionFieldAnchorP2PFieldValue as SchemaCollectionFieldAnchorP2PFieldValue,
	type CollectionFieldAnchorProviderLocation as SchemaCollectionFieldAnchorProviderLocation,
	type CollectionFieldAnchorProviderManual as SchemaCollectionFieldAnchorProviderManual,
	type CollectionFieldAnchorProviderPointToPoint as SchemaCollectionFieldAnchorProviderPointToPoint,
	type CollectionFieldFileTool as SchemaCollectionFieldFileTool,
	type CollectionFieldHeight as SchemaCollectionFieldHeight,
	type CollectionFieldHeightProviderManual as SchemaCollectionFieldHeightProviderManual,
	type CollectionFieldHeightProviderPhotomeasure as SchemaCollectionFieldHeightProviderPhotomeasure,
	type CollectionFieldHeightProviderPhotomeasureManual as SchemaCollectionFieldHeightProviderPhotomeasureManual,
	type CollectionFieldHeightProviderSpanHeight as SchemaCollectionFieldHeightProviderSpanHeight,
	type CollectionFieldImage as SchemaCollectionFieldImage,
	type CollectionFieldLocation as SchemaCollectionFieldLocation,
	type CollectionFieldNestedList as SchemaCollectionFieldNestedList,
	type CollectionFieldNumber as SchemaCollectionFieldNumber,
	type CollectionFieldOrientation as SchemaCollectionFieldOrientation,
	type CollectionFieldTrueSize as SchemaCollectionFieldTrueSize,
	type CollectionFieldVector as SchemaCollectionFieldVector,
	type CollectionFieldVectorP2PFieldValue as SchemaCollectionFieldVectorP2PFieldValue,
	type CollectionFieldVectorProviderCollection as SchemaCollectionFieldVectorProviderCollection,
	type CollectionFieldVectorProviderManual as SchemaCollectionFieldVectorProviderManual,
	type CollectionFieldVectorProviderPointToPoint as SchemaCollectionFieldVectorProviderPointToPoint,
	type CollectionFieldVerticalSeparation as SchemaCollectionFieldVerticalSeparation,
} from '@ikegps/core-api';
import { IkeFieldType } from '@ikegps/core-api';
import type { AnchorLocationData } from '@ikegps/core-api/src/common/anchor';
import type { FormModelField } from '../../form/form.field';
import { CollectionModelField } from '../collection.field';
import { CollectionModelFieldAnchor, type ModelFieldStateAnchorLocation } from '../fields/anchor.field';
import { CollectionModelFieldFileTool } from '../fields/file.tool.field';
import { CollectionModelFieldHeight } from '../fields/height.field';
import { CollectionModelFieldImage } from '../fields/image.field';
import { CollectionModelFieldLocation } from '../fields/location.field';
import { CollectionModelFieldNestedList } from '../fields/nested.list.field';
import { CollectionModelFieldNumber, CollectionModelFieldNumberSagUtil } from '../fields/number.field';
import { CollectionModelFieldOrientation } from '../fields/orientation.field';
import { CollectionModelFieldTrueSize } from '../fields/true.size.field';
import { CollectionModelFieldVector, type ModelFieldStateVectorCollection } from '../fields/vector.field';
import { CollectionModelFieldVerticalSeparation } from '../fields/vertical.separation.field';
import { CaptureOrientationSerializer } from './capture.orientation.read';

function writeNestedList(collectionField: CollectionModelFieldNestedList): SchemaCollectionFieldNestedList {
	if (collectionField.value == null) {
		return null;
	}

	return [collectionField.value];
}

const toUNIXTime = (date: Date) => date.getTime() / 1000;

function getLocations(capture: CaptureFileJSONObject): CaptureLocation {
	if (capture.target == null && capture.device == null) {
		return undefined;
	}

	return {
		target: capture.target,
		device: capture.device,
	};
}

function getLaser(capture: CaptureFileJSONObject) {
	if (capture.distance == null) {
		return undefined;
	}

	const output = {
		distance: capture.distance,
	};

	if (capture.raw && capture.raw.laser) {
		output['raw'] = capture.raw.laser;
	}
	return output;
}

function getCalibration(calibration: CalibrationJSONObject): CaptureCameraCalibration {
	if (calibration == null) {
		return undefined;
	}

	return {
		center: calibration.center,
		distortion: calibration.distortion,
		focal: calibration.focal,
		rotation: calibration.rotation,
		translation: calibration.translation,
	};
}

function writeCapture(field: CollectionModelField, capture: CaptureFileJSONObject, type: string): SchemaCaptureCapture {
	const imageType = type === 'image' ? 'image' : 'truesize';
	const calibration = field.collection.state.getCalibration(capture.calibrationid);
	const captureRaw = capture.raw || {};

	return {
		capture: {
			objectId: capture.objectId,
			camera: {
				calibration: getCalibration(calibration),
				imageuuid: capture.uuid,
				zoom: capture.zoom,
			},
			device: calibration != null ? calibration.deviceid : undefined,
			timestamp: capture.collectedAt && toUNIXTime(new Date(capture.collectedAt.iso)),
			type: imageType,
			uuid: captureRaw.uuid || capture.uuid,
			laser: getLaser(capture),
			locations: getLocations(capture),
			orientation: CaptureOrientationSerializer.toLegacyOrientation(capture),
		},
	};
}

function writeImageField(collectionField: CollectionModelFieldImage): SchemaCollectionFieldImage {
	return collectionField.getCaptures().map((capture) => writeCapture(collectionField, capture, 'image'));
}

function writeTrueSizeField(collectionField: CollectionModelFieldTrueSize): SchemaCollectionFieldTrueSize {
	return collectionField.getCaptures().map((capture) => writeCapture(collectionField, capture, 'truesize'));
}

function writePMHeight(collectionField: CollectionModelFieldHeight): SchemaCollectionFieldHeightProviderPhotomeasure {
	const collection = collectionField.collection;
	const stateValue = collectionField.stateValue;
	const drawable = collection.getDrawableState(stateValue.captureId).getDrawable(stateValue.drawable);
	const capture = collection.state.getCapture(drawable.captureId);
	if (capture == null) {
		throw new Error(
			`Field ${collectionField.id}: Unable to find capture: ${drawable.captureId} for drawable: ${drawable.id}`
		);
	}
	return {
		provider: 'photomeasure',
		value: collectionField.value,
		measurement: capture.objectId,
		drawable: stateValue.drawable,
	};
}

function writePMManHeight(
	collectionField: CollectionModelFieldHeight
): SchemaCollectionFieldHeightProviderPhotomeasureManual {
	const collection = collectionField.collection;
	const stateValue = collectionField.stateValue;
	const drawable = collection.getDrawableState(stateValue.captureId).getDrawable(stateValue.drawable);
	const capture = collection.state.getCapture(drawable.captureId);
	if (capture == null) {
		throw new Error(
			`Field ${collectionField.id}: Unable to find capture: ${drawable.captureId} for drawable: ${drawable.id}`
		);
	}
	return {
		provider: 'photomeasuremanual',
		value: collectionField.value,
		measurement: capture.objectId,
		drawable: stateValue.drawable,
	};
}

function writeVerticalSeparation(
	collectionField: CollectionModelFieldVerticalSeparation
): SchemaCollectionFieldVerticalSeparation {
	const collection = collectionField.collection;
	const stateValue = collectionField.stateValue;
	if (!stateValue?.captureId || !stateValue?.drawableTop) {
		return {
			value: collectionField.value,
		};
	}
	const drawable = collection.getDrawableState(stateValue.captureId).getDrawable(stateValue.drawableTop);
	const capture = collection.state.getCapture(drawable.captureId);
	if (capture == null) {
		throw new Error(
			`Field ${collectionField.id}: Unable to find capture: ${drawable.captureId} for drawable: ${drawable.id}`
		);
	}
	return {
		value: collectionField.value,
		measurement: capture.objectId,
		drawableTop: stateValue?.drawableTop,
		drawableBottom: stateValue?.drawableBottom,
		drawable: stateValue?.drawableVerticalSeparation,
	};
}

function writeSpanHeight(collectionField: CollectionModelFieldHeight): SchemaCollectionFieldHeightProviderSpanHeight {
	return {
		provider: 'spanheight',
		value: collectionField.value,
		capture: {
			type: 'spanheight',
			points: collectionField.getCaptures().map((capture) => writeCapture(collectionField, capture, 'spanheight')),
		},
	};
}

function writeManualHeight(collectionField: CollectionModelFieldHeight): SchemaCollectionFieldHeightProviderManual {
	return {
		provider: 'manual',
		value: collectionField.value,
	};
}

function writeHeight(collectionField: CollectionModelFieldHeight): SchemaCollectionFieldHeight {
	if (collectionField.isManual()) {
		return writeManualHeight(collectionField);
	}
	if (collectionField.isSpanHeight()) {
		return writeSpanHeight(collectionField);
	}
	if (collectionField.isPhotoMeasureManual()) {
		return writePMManHeight(collectionField);
	}
	if (collectionField.isPhotoMeasure()) {
		return writePMHeight(collectionField);
	}

	throw new Error(`Field ${collectionField.id}: Invalid Height provider "${collectionField.stateValue.provider}"`);
}

function writeManualVector(collectionField: CollectionModelFieldVector): SchemaCollectionFieldVectorProviderManual {
	return {
		provider: 'manual',
		value: collectionField.value,
	};
}

function writeP2PVector(collectionField: CollectionModelFieldVector): SchemaCollectionFieldVectorProviderPointToPoint {
	return {
		capture: {
			type: 'pointtopoint',
			points: collectionField.getCaptures().map((capture) => writeCapture(collectionField, capture, 'pointtopoint')),
		},
		provider: 'pointtopoint',
		value: <SchemaCollectionFieldVectorP2PFieldValue>collectionField.value,
	};
}

function writeCollectionVector(
	collectionField: CollectionModelField<FormModelField, ModelFieldStateVectorCollection>
): SchemaCollectionFieldVectorProviderCollection {
	return {
		provider: 'collection',
		collectionId: collectionField.stateValue.collectionId,
		value: collectionField.value,
	};
}

function writeVector(collectionField: CollectionModelFieldVector): SchemaCollectionFieldVector {
	if (collectionField.isManual()) {
		return writeManualVector(collectionField);
	}

	if (collectionField.isPointToPoint()) {
		return writeP2PVector(collectionField);
	}

	if (collectionField.isCollectionLink()) {
		return writeCollectionVector(collectionField);
	}

	throw new Error(`Field ${collectionField.id}: Invalid Vector provider "${collectionField.stateValue.provider}"`);
}

function writeManualAnchor(collectionField: CollectionModelFieldAnchor): SchemaCollectionFieldAnchorProviderManual {
	const value = { ...(<CollectionFieldAnchorManualFieldValue>collectionField.value) };

	if (collectionField.isBearingDerived()) {
		delete value.bearing;
	}

	return {
		provider: CollectionModelFieldAnchor.PROVIDER_MANUAL,
		value,
	};
}

function writeGpsAnchor(collectionField: CollectionModelFieldAnchor): SchemaCollectionFieldAnchorProviderLocation {
	const stateValue = <ModelFieldStateAnchorLocation>collectionField.stateValue;

	return {
		provider: CollectionModelFieldAnchor.PROVIDER_GPS,
		value: collectionField.value as AnchorLocationData,
		antennaHeight: collectionField.antennaHeight,
		fix: stateValue.fix,
		module: stateValue.module,
		capturedAt: stateValue.capturedAt,
		undulation: stateValue.undulation,
	};
}

function writeManualLocationAnchor(
	collectionField: CollectionModelFieldAnchor
): SchemaCollectionFieldAnchorProviderLocation {
	return {
		provider: CollectionModelFieldAnchor.PROVIDER_MANUAL,
		value: collectionField.value as AnchorLocationData,
	};
}

function writeP2PAnchor(collectionField: CollectionModelFieldAnchor): SchemaCollectionFieldAnchorProviderPointToPoint {
	return {
		capture: {
			type: CollectionModelFieldAnchor.PROVIDER_P2P,
			points: collectionField.getCaptures().map((capture) => writeCapture(collectionField, capture, 'pointtopoint')),
		},
		provider: CollectionModelFieldAnchor.PROVIDER_P2P,
		value: <SchemaCollectionFieldAnchorP2PFieldValue>collectionField.value,
	};
}

function writeAnchor(collectionField: CollectionModelFieldAnchor): SchemaCollectionFieldAnchor {
	if (!collectionField.hasValue()) {
		return undefined;
	}

	if (collectionField.tool === CollectionModelFieldAnchor.COLLLECTION_TOOL_LOCATION) {
		if (collectionField.isGPS()) {
			return writeGpsAnchor(collectionField);
		}
		if (collectionField.isManual()) {
			return writeManualLocationAnchor(collectionField);
		}
	}

	if (collectionField.tool === CollectionModelFieldAnchor.COLLLECTION_TOOL_VECTOR) {
		if (collectionField.isPointToPoint()) {
			return writeP2PAnchor(collectionField);
		}
		if (collectionField.isManual()) {
			return writeManualAnchor(collectionField);
		}
	}

	throw new Error(`Field ${collectionField.id}: Invalid Anchor provider "${collectionField.stateValue.provider}"`);
}

function writeNumber(collectionField: CollectionModelFieldNumber): SchemaCollectionFieldNumber {
	if (collectionField.isManual()) {
		return String(collectionField.value);
	}

	if (collectionField.isBearing()) {
		return {
			provider: CollectionModelFieldNumber.PROVIDER_BEARING,
			capture: writeCapture(collectionField, collectionField.getCaptures()[0], 'truesize').capture,
			value: collectionField.value,
		};
	}

	if (collectionField.isBackBearing()) {
		return {
			provider: CollectionModelFieldNumber.PROVIDER_BACK_BEARING,
			capture: writeCapture(collectionField, collectionField.getCaptures()[0], 'truesize').capture,
			value: collectionField.value,
		};
	}

	if (collectionField.isSag()) {
		const sagCaptures = CollectionModelFieldNumberSagUtil.getSagCaptures(collectionField);
		return {
			provider: CollectionModelFieldNumber.PROVIDER_SAG,
			value: collectionField.value,
			start: writeCapture(collectionField, sagCaptures.start, 'truesize').capture,
			end: writeCapture(collectionField, sagCaptures.end, 'truesize').capture,
			points: sagCaptures.points.map((p) => writeCapture(collectionField, p, 'truesize').capture),
		};
	}

	throw new Error(`Field ${collectionField.id}: Invalid Number provider "${collectionField.stateValue.provider}"`);
}

function writeLocation(collectionField: CollectionModelFieldLocation): SchemaCollectionFieldLocation {
	if (!collectionField.hasValue()) {
		return undefined;
	}

	if (collectionField.isEdited()) {
		return {
			provider: 'edited',
			value: collectionField.value,
		};
	}

	if (collectionField.isManual()) {
		return {
			provider: 'manual',
			value: collectionField.value,
		};
	}

	if (collectionField.isGPS()) {
		const stateValue = collectionField.stateValue;
		return {
			provider: 'gps',
			value: stateValue.value,
			fix: stateValue.fix,
			antennaHeight: stateValue.antennaHeight,
			module: stateValue.module,
			capturedAt: stateValue.capturedAt,
			undulation: stateValue.undulation,
			datum: stateValue.datum,
		};
	}

	if (collectionField.isTrueSize()) {
		const [capture] = collectionField.getCaptures();
		return {
			provider: 'truesize',
			value: collectionField.value,
			capture: writeCapture(collectionField, capture, 'truesize').capture,
			datum: collectionField.stateValue.datum,
		};
	}

	throw new Error(`Field ${collectionField.id}: Invalid Location provider "${collectionField.stateValue.provider}"`);
}
function writeOrientation(collectionField: CollectionModelFieldOrientation): SchemaCollectionFieldOrientation {
	if (collectionField.isManual()) {
		return String(collectionField.value);
	}

	if (collectionField.isBearing()) {
		return {
			provider: CollectionModelFieldOrientation.PROVIDER_BEARING,
			capture: writeCapture(collectionField, collectionField.getCaptures()[0], 'truesize').capture,
			value: collectionField.value,
		};
	}

	if (collectionField.isBackBearing()) {
		return {
			provider: CollectionModelFieldOrientation.PROVIDER_BACK_BEARING,
			capture: writeCapture(collectionField, collectionField.getCaptures()[0], 'truesize').capture,
			value: collectionField.value,
		};
	}

	throw new Error(`Field ${collectionField.id}: Invalid Orientation provider "${collectionField.stateValue.provider}"`);
}

function writeField(collectionField: CollectionModelField): SchemaCollectionField {
	return collectionField.value;
}

function writeFileToolField(collectionField: CollectionModelFieldFileTool): SchemaCollectionFieldFileTool {
	return {
		filename: collectionField.stateValue.filename,
		attachmentId: collectionField.stateValue.attachmentId,
		size: collectionField.stateValue.size,
		metadata: collectionField.stateValue.metadata,
	};
}

export const FieldWriters: { [key: string]: (field: CollectionModelField) => CollectionModelField['value'] } = {
	[IkeFieldType.DATE]: writeField,
	[IkeFieldType.LOCATION]: writeLocation,
	[IkeFieldType.NESTED_LIST]: writeNestedList,
	[IkeFieldType.NUMBER]: writeNumber,
	[IkeFieldType.ORIENTATION]: writeOrientation,
	[IkeFieldType.SELECT_LIST]: writeField,
	[IkeFieldType.TEXT]: writeField,
	[IkeFieldType.HEIGHT]: writeHeight,
	[IkeFieldType.TRUE_SIZE]: writeTrueSizeField,
	[IkeFieldType.YES_NO]: writeField,
	[IkeFieldType.IMAGE]: writeImageField,
	[IkeFieldType.VECTOR]: writeVector,
	[IkeFieldType.ANCHOR]: writeAnchor,
	[IkeFieldType.VERTICAL_SEPARATION]: writeVerticalSeparation,
	[IkeFieldType.FILE_TOOL]: writeFileToolField,
};
